import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Send, Link2, MessageCircle, FileDown, Loader2 } from "lucide-react";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { waLink, fmtDateTime } from "@/lib/crm";

const STATUS = { in_attesa: "In attesa di firma", firmata: "Firmata", sostituita: "Sostituita" };

const openPdf = async (rid) => {
  try {
    const r = await api.get(`/privacy/${rid}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Download non riuscito"); }
};

export const PrivacyCard = ({ client }) => {
  const { data: reqs = [] } = useApi(`/clients/${client.client_id}/privacy`);
  const { data: cp } = useApi(`/clients/${client.client_id}/consent-proof`);
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const signed = reqs.find((r) => r.status === "firmata");
  const pending = reqs.find((r) => r.status === "in_attesa");

  const create = async (sendEmail) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.client_id}/privacy-request`, { public_base: window.location.origin, send_email: sendEmail });
      refresh();
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); }
  };
  const sendMail = async () => { const d = await create(true); if (d) toast.success(d.email_sent ? "Richiesta di firma inviata via email" : "Link creato (email non inviata)"); };
  const copyLink = async () => {
    const d = await create(false);
    if (!d) return;
    try { await navigator.clipboard.writeText(d.url); } catch { window.prompt("Copia il link", d.url); }
    toast.success("Link di firma copiato");
  };
  const shareWa = async () => {
    const d = await create(false);
    if (d) window.open(waLink(client.phone, `Buongiorno ${client.first_name}, per procedere con la sua richiesta le chiedo di leggere e firmare l'informativa privacy qui: ${d.url}`), "_blank");
  };

  return (
    <div data-testid="privacy-card" className={`rounded-xl border p-5 flex flex-wrap items-center justify-between gap-4 ${signed ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`}>
      <div className="flex items-start gap-3 min-w-0">
        {signed ? <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5" /> : <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5" />}
        <div>
          <p className="font-display font-semibold text-sm" data-testid="privacy-status">
            {signed ? `Privacy firmata il ${fmtDateTime(signed.signed_at)}` : pending ? "Privacy: in attesa di firma del cliente" : "Privacy non ancora firmata"}
          </p>
          {signed && <p className="text-xs text-slate-600 mt-0.5">Dati salute: {signed.consents?.particolari ? "sì" : "no"} · Marketing: {signed.consents?.marketing ? "sì" : "no"}</p>}
          {cp?.proof && <p data-testid="consent-proof" className="text-xs text-slate-600 mt-0.5">Consenso marketing: documento n. {cp.proof.request_id} firmato il {fmtDateTime(cp.proof.signed_at)} · titolare {(cp.proof.titolare || "").split("\n")[0]} · finalità: {cp.purpose} · canali: {cp.proof.channels.join(", ")} ({cp.proof.scope})</p>}
          {pending && !signed && <p className="text-xs text-slate-500 mt-0.5">Richiesta del {fmtDateTime(pending.created_at)} · {STATUS[pending.status]}{pending.opened_at ? " · link aperto" : ""}</p>}
          {(client.chat_notice_log || []).map((l) => (
            <p key={l.at} data-testid="chat-notice-log-entry" className="text-xs text-slate-600 mt-0.5">Informativa chat AI letta il {fmtDateTime(l.at)} · versione del {l.version_date || "—"} · impronta {(l.version_hash || "").slice(0, 12)}</p>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {signed && <button data-testid="privacy-pdf-btn" onClick={() => openPdf(signed.request_id)} className="flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3.5 py-1.5 text-xs font-medium hover:bg-emerald-700 transition-colors"><FileDown className="h-3.5 w-3.5" />PDF firmato</button>}
        <button data-testid="privacy-send-email-btn" disabled={busy || !client.email} onClick={sendMail} title={client.email ? "" : "Il cliente non ha email"} className="flex items-center gap-1.5 rounded-full bg-[#0F172A] text-white px-3.5 py-1.5 text-xs font-medium hover:bg-sky-600 disabled:opacity-40 transition-colors">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{signed ? "Nuova firma via email" : "Richiedi firma via email"}</button>
        <button data-testid="privacy-copy-link-btn" disabled={busy} onClick={copyLink} className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs hover:border-sky-500 transition-colors"><Link2 className="h-3.5 w-3.5" />Copia link</button>
        <button data-testid="privacy-wa-btn" disabled={busy || !client.phone} onClick={shareWa} className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs hover:border-emerald-500 disabled:opacity-40 transition-colors"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</button>
      </div>
    </div>
  );
};
